import { TextField } from '@material-ui/core';
import { Home } from '@material-ui/icons';
import { Menu } from '@mui/icons-material';
import { Autocomplete, CardContent, Typography } from '@mui/material';
import { ascend } from 'ramda';
import { useMemo } from 'react';

import { Icon } from 'shared/components/ui';
import { SchoolConfigurationLayout } from 'shared/graphql';

import { MainNavigationCard } from '../MainNavigationCard/MainNavigationCard';
import { PreviewsContainer, PreviewsItem } from './MainNavigationMobileNav.styles';

interface Props {
  items: SchoolConfigurationLayout[];
  availableOptions: SchoolConfigurationLayout[];
  onListUpdate(items: SchoolConfigurationLayout[]): void;
}

const CUSTOMIZABLE_ITEMS_LENGTH = 3;

export function MainNavigationMobileNav(props: Props) {
  const menu = useMemo(() => {
    const pages = [...props.items];

    return pages
      .sort(ascend(page => page?.navbarOrderNumber as number))
      .slice(0, CUSTOMIZABLE_ITEMS_LENGTH);
  }, [props.items]);

  const onChange = (prev: SchoolConfigurationLayout | undefined, position: number) => {
    return (event: unknown, newest: SchoolConfigurationLayout | null) => {
      if (!newest) {
        return;
      }

      const sorted = props.items.map(item => {
        if (item.id === newest?.id) {
          return { ...item, navbarOrderNumber: position };
        }

        if (item.id === prev?.id) {
          return { ...item, navbarOrderNumber: newest?.navbarOrderNumber };
        }

        return item;
      });

      props.onListUpdate(sorted);
    };
  };

  return (
    <MainNavigationCard
      title="Mobile Navbar"
      subtitle="Customize the 3 central items on the mobile nav"
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {menu.map((item, index) => (
          <Autocomplete
            key={item?.id ?? index}
            value={item}
            options={props.availableOptions}
            getOptionLabel={option => option.name as string}
            isOptionEqualToValue={(option, item) => option.id === item?.id}
            onChange={onChange(item, index)}
            renderInput={params => (
              <TextField {...params} variant="outlined" label={`Item #${index + 1}`} />
            )}
          />
        ))}

        <Typography variant="body2" color="textSecondary">
          Preview
        </Typography>

        <PreviewsContainer>
          <PreviewsItem>
            <Home />
          </PreviewsItem>

          {menu
            .filter(item => item.icon)
            .map((item, index) => (
              <PreviewsItem key={item.id ?? index}>
                <Icon name={item.icon!} />
              </PreviewsItem>
            ))}

          <PreviewsItem>
            <Menu />
          </PreviewsItem>
        </PreviewsContainer>
      </CardContent>
    </MainNavigationCard>
  );
}
